import { Item, NameDependency, Option } from "@/types/productTypes";

export function computeValidItemsForOption(
   option: Option,
   watchedValues: Record<string, any>,
   translateNameChange: (nameDependency: NameDependency) => string
) {
   const dependenciesMap = new Map<number, any>();

   // Gather dependencies from the option's dependent item managers
   option.dependentItemManagers?.forEach((manager: any) => {
      const affectingOptionId = manager.affectingOptionId;
      if (!affectingOptionId) return;

      // Get the watched value
      const watchedValue = watchedValues[affectingOptionId.toString()];
      if (!watchedValue || !("selectedItem" in watchedValue)) return;
      if (watchedValue.selected) {
         dependenciesMap.set(affectingOptionId, watchedValue);
      }
   });

   const validItems = option.items?.map((item: any) => {
      // If no dependency, item remains unchanged
      if (
         !item.dependentDependencyManager ||
         item.dependentDependencyManager.length === 0
      ) {
         return {
            ...item,
            disabled: false,
            name: item.name, // might rename as `modifiedName` if you prefer
         };
      }

      // Track if item is disabled
      const affectingOptionsMap = new Map<number, boolean>();
      let totalPriceAdjustment = 0;
      let nameChange: string | null = null;
      let learnMoreId: number | null = null;

      item.dependentDependencyManager.forEach((dep: any) => {
         const {
            affectingItemId,
            affectingOptionId,
            priceDependency,
            nameDependency,
            learnMoreDependency,
         } = dep;
         if (!affectingOptionId) return;
         const watchedOption = dependenciesMap.get(affectingOptionId);
         if (!watchedOption) {
            affectingOptionsMap.set(affectingOptionId, false);
            return;
         }
         if (!affectingItemId)
            throw new Error("Affecting Item Id is undefined");

         // Check if the selected item(s) match
         const selectedItem = watchedOption.selectedItem;
         const isValid = Array.isArray(selectedItem)
            ? selectedItem.includes(affectingItemId.toString())
            : selectedItem === affectingItemId.toString();

         if (isValid) {
            affectingOptionsMap.set(affectingOptionId, true);

            // Price adjustments
            if (priceDependency?.priceAdjustment) {
               totalPriceAdjustment = priceDependency.priceAdjustment;
            }

            // Name changes
            if (nameDependency?.nameChange) {
               nameChange = translateNameChange(nameDependency);
            }

            // Learn More content
            if (learnMoreDependency?.learnMoreId) {
               learnMoreId = learnMoreDependency.learnMoreId;
            }
         } else if (!affectingOptionsMap.has(affectingOptionId)) {
            affectingOptionsMap.set(affectingOptionId, false);
         }
      });

      const isDisabled = [...affectingOptionsMap.values()].includes(false);
      return {
         ...item,
         disabled: isDisabled,
         price: totalPriceAdjustment || item.price,
         name: nameChange || item.name,
         learnMoreId: learnMoreId || item.learnMoreId,
      };
   });

   return validItems || [];
}

/**
 * Ensures the selected item is not disabled; if it is, fall back to a valid
 * default.
 */
export function updateDisabledSelection(
   optionId: number,
   validItems: Item[],
   watchedValue: any,
   setValue: (fieldName: string, value: any) => void
) {
   if (!watchedValue || !("selectedItem" in watchedValue)) return;

   const watchedSelectedItem = watchedValue.selectedItem;
   // If any of the currently watched selection is disabled, pick fallback
   const isDisabled = validItems.some(
      (item) =>
         (Array.isArray(watchedSelectedItem)
            ? watchedSelectedItem.includes(item.id?.toString())
            : item.id?.toString() === watchedSelectedItem) && item.disabled
   );

   if (isDisabled) {
      // Find fallback item
      const fallbackDefault = validItems.find((item) => !item.disabled);
      if (fallbackDefault && fallbackDefault.id) {
         setValue(optionId.toString(), {
            selectedItem: fallbackDefault.id.toString(),
            selected: true,
         });
      }
   }
}
export function traverseGraph(
   graph: any,
   startNodeId: number,
   allValues: Record<string, any>,
   visited = new Set<number>()
) {
   if (!graph[startNodeId]) return [];
   if (visited.has(startNodeId)) return [];

   visited.add(startNodeId);
   const currentNode = graph[startNodeId];
   const collectedImages: any[] = []; // array of arrays or just images?

   // Collect images if itemId is in allValues
   const itemIdsWeCareAbout = new Set(
      Object.values(allValues).map((val) =>
         "selectedItem" in val ? val.selectedItem : null
      )
   );

   for (const imgNode of currentNode.imagesInNode) {
      if (
         imgNode.itemId?.toString() &&
         itemIdsWeCareAbout.has(imgNode.itemId.toString())
      ) {
         collectedImages.push([imgNode.image]);
      }
   }

   // Traverse outgoing edges
   if (currentNode.outgoingEdges && currentNode.outgoingEdges.length > 0) {
      // If the next node is a CONNECTOR, keep going
      if (currentNode.outgoingEdges[0].targetNode.type === "CONNECTOR") {
         const targetNodeId = currentNode.outgoingEdges[0].targetNode.id;
         const imagesFromTarget = traverseGraph(
            graph,
            targetNodeId,
            allValues,
            visited
         );
         collectedImages.push(...imagesFromTarget);
      }

      for (const edge of currentNode.outgoingEdges) {
         if (edge.conditionItems.length > 0) {
            const firstCondItem = edge.conditionItems[0];
            if (firstCondItem.optionId && allValues[firstCondItem.optionId]) {
               const itemIdToMatch = allValues[firstCondItem.optionId];
               if (!("selectedItem" in itemIdToMatch)) {
                  throw new Error("No 'selectedItem' in watchedValues");
               }
               const foundMatch = edge.conditionItems.some(
                  (ci: any) =>
                     ci.id && ci.id.toString() === itemIdToMatch.selectedItem
               );
               if (foundMatch && itemIdToMatch.selected) {
                  const imagesFromTarget = traverseGraph(
                     graph,
                     edge.targetNodeId,
                     allValues,
                     visited
                  );
                  collectedImages.push(...imagesFromTarget);
               }
            }
         }
      }
   }

   return collectedImages;
}
